import { forwardRef, type HTMLAttributes } from "react";
import { getBadgeInfo, type DisciplineSlug } from "@jumpbean/shared";

export type BadgeSize = "sm" | "md" | "lg";

export interface BadgeProps extends HTMLAttributes<HTMLDivElement> {
  /** Discipline this badge belongs to */
  discipline: DisciplineSlug;
  /** Level number (1-5) */
  level: number;
  /** Whether the badge has been earned */
  isEarned?: boolean;
  /** Size of the badge */
  size?: BadgeSize;
  /** Whether to show the badge name label */
  showLabel?: boolean;
  /** Whether to animate the badge (for newly earned) */
  animate?: boolean;
}

/**
 * Badge component for displaying achievement badges.
 * Each discipline has 5 levels with unique badges.
 *
 * @example
 * // Earned math badge
 * <Badge discipline="math" level={1} isEarned showLabel />
 *
 * @example
 * // Locked badge (not yet earned)
 * <Badge discipline="spelling" level={3} isEarned={false} />
 *
 * @example
 * // Newly earned badge with celebration animation
 * <Badge discipline="patterns" level={2} isEarned animate />
 */
export const Badge = forwardRef<HTMLDivElement, BadgeProps>(
  (
    {
      discipline,
      level,
      isEarned = false,
      size = "md",
      showLabel = false,
      animate = false,
      className = "",
      ...props
    },
    ref
  ) => {
    const badgeInfo = getBadgeInfo(discipline, level);
    const badgeName = badgeInfo?.name ?? `Level ${level}`;
    const badgeDescription = badgeInfo?.description ?? "";

    // Size configurations
    const sizeStyles = {
      sm: {
        container: "w-14 h-14",
        icon: "w-8 h-8",
        star: "w-3 h-3",
        label: "text-body-md",
      },
      md: {
        container: "w-20 h-20",
        icon: "w-12 h-12",
        star: "w-4 h-4",
        label: "text-body-md",
      },
      lg: {
        container: "w-28 h-28",
        icon: "w-16 h-16",
        star: "w-5 h-5",
        label: "text-body-lg",
      },
    };

    // Discipline colors for badge backgrounds
    const disciplineColors: Record<DisciplineSlug, { bg: string; accent: string; icon: string }> = {
      math: {
        bg: "from-luna-light via-luna to-magic-400",
        accent: "border-magic-500",
        icon: "text-magic-700",
      },
      spelling: {
        bg: "from-petal-light via-petal to-meadow-400",
        accent: "border-meadow-600",
        icon: "text-meadow-700",
      },
      patterns: {
        bg: "from-prism-red via-prism-yellow to-prism-blue",
        accent: "border-prism-red",
        icon: "text-white",
      },
      typing: {
        bg: "from-whiskers-light via-whiskers to-sky-600",
        accent: "border-sky-700",
        icon: "text-white",
      },
      programming: {
        bg: "from-pixel-light via-pixel to-pixel-darker",
        accent: "border-pixel-dark",
        icon: "text-white",
      },
    };

    // Badge icons by discipline
    const disciplineIcons: Record<DisciplineSlug, React.ReactNode> = {
      math: <StarBurstIcon className={sizeStyles[size].icon} />,
      spelling: <FlowerIcon className={sizeStyles[size].icon} />,
      patterns: <RainbowIcon className={sizeStyles[size].icon} />,
      typing: <WandIcon className={sizeStyles[size].icon} />,
      programming: <GearIcon className={sizeStyles[size].icon} />,
    };

    const colors = disciplineColors[discipline];
    const currentSize = sizeStyles[size];

    return (
      <div
        ref={ref}
        className={`
          inline-flex flex-col items-center gap-2
          ${className}
        `}
        {...props}
      >
        {/* Badge circle */}
        <div
          className={`
            relative
            ${currentSize.container}
            rounded-full
            flex items-center justify-center
            transition-all duration-300
            ${
              isEarned
                ? `
                    bg-gradient-to-br ${colors?.bg ?? ""}
                    border-[3px] ${colors?.accent ?? ""}
                    shadow-[0_6px_20px_rgba(0,0,0,0.15),inset_0_-3px_8px_rgba(0,0,0,0.1)]
                    ${animate ? "animate-celebrate" : ""}
                  `
                : `
                    bg-gradient-to-br from-neutral-200 to-neutral-300
                    border-[3px] border-neutral-400
                    shadow-[0_4px_12px_rgba(0,0,0,0.1)]
                    opacity-50
                    grayscale
                  `
            }
          `}
          role="img"
          aria-label={`${badgeName} badge - ${isEarned ? "Earned" : "Locked"}`}
        >
          {/* Badge icon */}
          <div className={isEarned ? (colors?.icon ?? "") : "text-neutral-500"}>
            {disciplineIcons[discipline]}
          </div>

          {/* Level number badge */}
          <div
            className={`
              absolute -top-1 -right-1
              w-7 h-7
              rounded-full
              flex items-center justify-center
              font-display font-bold text-body-md
              border-2 border-cloud
              shadow-sm
              ${
                isEarned
                  ? "bg-sunshine-400 text-neutral-900"
                  : "bg-neutral-400 text-neutral-600"
              }
            `}
          >
            {level}
          </div>

          {/* Sparkle decorations for earned badges */}
          {isEarned && (
            <>
              <SparkleDecoration
                className="absolute -top-2 -left-1 w-4 h-4 text-sunshine-300"
                delay={0}
              />
              <SparkleDecoration
                className="absolute -bottom-1 -right-2 w-3 h-3 text-sunshine-400"
                delay={500}
              />
              <SparkleDecoration
                className="absolute top-1 -right-3 w-2 h-2 text-sunshine-200"
                delay={250}
              />
            </>
          )}

          {/* Lock icon for unearned */}
          {!isEarned && (
            <div className="absolute inset-0 flex items-center justify-center bg-neutral-900/20 rounded-full">
              <LockIcon className="w-6 h-6 text-neutral-500" />
            </div>
          )}
        </div>

        {/* Badge label */}
        {showLabel && (
          <div className="text-center max-w-[120px]">
            <p
              className={`
                font-display font-semibold
                ${currentSize.label}
                ${isEarned ? "text-neutral-800" : "text-neutral-500"}
                truncate
              `}
            >
              {badgeName}
            </p>
            {isEarned && badgeDescription && size === "lg" && (
              <p className="text-body-md text-neutral-500 mt-0.5 line-clamp-2">
                {badgeDescription}
              </p>
            )}
          </div>
        )}
      </div>
    );
  }
);

Badge.displayName = "Badge";

// Badge icons
function StarBurstIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M12 0L14.59 8.41L23 11L14.59 13.59L12 22L9.41 13.59L1 11L9.41 8.41L12 0Z" />
    </svg>
  );
}

function FlowerIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <circle cx="12" cy="12" r="3" />
      <ellipse cx="12" cy="5" rx="3" ry="4" />
      <ellipse cx="12" cy="19" rx="3" ry="4" />
      <ellipse cx="5" cy="12" rx="4" ry="3" />
      <ellipse cx="19" cy="12" rx="4" ry="3" />
      <ellipse cx="7" cy="7" rx="3" ry="3" transform="rotate(-45 7 7)" />
      <ellipse cx="17" cy="17" rx="3" ry="3" transform="rotate(-45 17 17)" />
      <ellipse cx="17" cy="7" rx="3" ry="3" transform="rotate(45 17 7)" />
      <ellipse cx="7" cy="17" rx="3" ry="3" transform="rotate(45 7 17)" />
    </svg>
  );
}

function RainbowIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
      <path d="M2 17a10 10 0 0 1 20 0" strokeLinecap="round" />
      <path d="M5 17a7 7 0 0 1 14 0" strokeLinecap="round" />
      <path d="M8 17a4 4 0 0 1 8 0" strokeLinecap="round" />
    </svg>
  );
}

function WandIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M15 4l-11 11 4 4 11-11-4-4z" />
      <path d="M20.5 3.5L18 6l-1.5-1.5 2.5-2.5c.4-.4 1-.4 1.4 0l.1.1c.4.4.4 1 0 1.4z" />
      <circle cx="6" cy="4" r="1" />
      <circle cx="4" cy="8" r="1" />
      <circle cx="9" cy="3" r="1" />
    </svg>
  );
}

function GearIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M12 15a3 3 0 1 0 0-6 3 3 0 0 0 0 6z" />
      <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z" />
    </svg>
  );
}

function LockIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
      <rect x="3" y="11" width="18" height="11" rx="2" />
      <path d="M7 11V7a5 5 0 0 1 10 0v4" />
    </svg>
  );
}

function SparkleDecoration({
  className,
  delay = 0,
}: {
  className?: string;
  delay?: number;
}) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="currentColor"
      style={{ animationDelay: `${delay}ms` }}
      aria-hidden="true"
    >
      <path
        className="animate-sparkle"
        d="M12 0L14.59 8.41L23 11L14.59 13.59L12 22L9.41 13.59L1 11L9.41 8.41L12 0Z"
      />
    </svg>
  );
}

export default Badge;
